import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Play, RotateCcw, Sparkles, Radio, Users, CheckCircle, 
  Banknote, Calendar, MapPin, Clock, Euro, Zap, Heart,
  PartyPopper, TrendingUp, ArrowRight
} from 'lucide-react';
import { Button } from './ui/button';
import { useTranslation } from '../i18n';

// Brand colors
const COLORS = {
  darkBlue: '#1a2d56',
  green: '#d7de3b',
  greenDark: '#b8bf2f',
};

// Participant avatars for the simulation
const PARTICIPANTS = [
  { name: 'Marie', avatar: 'https://api.dicebear.com/7.x/avataaars/svg?seed=Marie123&backgroundColor=b6e3f4' },
  { name: 'Thomas', avatar: 'https://api.dicebear.com/7.x/avataaars/svg?seed=Thomas456&backgroundColor=c0aede' },
  { name: 'Sophie', avatar: 'https://api.dicebear.com/7.x/avataaars/svg?seed=Sophie789&backgroundColor=d1d4f9' },
  { name: 'Lucas', avatar: 'https://api.dicebear.com/7.x/avataaars/svg?seed=Lucas321&backgroundColor=ffd5dc' },
  { name: 'Emma', avatar: 'https://api.dicebear.com/7.x/avataaars/svg?seed=Emma654&backgroundColor=ffdfbf' },
];

// Confetti component
const Confetti = ({ count = 50 }) => {
  const confettiColors = ['#d7de3b', '#FFD700', '#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FFEAA7'];
  
  return (
    <div className="absolute inset-0 pointer-events-none overflow-hidden">
      {[...Array(count)].map((_, i) => (
        <motion.div
          key={i}
          className="absolute w-3 h-3 rounded-sm"
          style={{
            backgroundColor: confettiColors[i % confettiColors.length],
            left: `${Math.random() * 100}%`,
            top: '-10px',
          }}
          initial={{ y: -20, rotate: 0, opacity: 1 }}
          animate={{
            y: 400,
            rotate: Math.random() * 720 - 360,
            opacity: [1, 1, 0],
          }}
          transition={{
            duration: 2 + Math.random() * 2,
            delay: Math.random() * 0.5,
            ease: "easeOut",
          }}
        />
      ))}
    </div>
  );
};

// Money particles
const MoneyParticles = ({ count = 20 }) => {
  return (
    <div className="absolute inset-0 pointer-events-none overflow-hidden">
      {[...Array(count)].map((_, i) => (
        <motion.div
          key={i}
          className="absolute text-2xl"
          style={{
            left: `${20 + Math.random() * 60}%`,
            bottom: '0%',
          }}
          initial={{ y: 0, opacity: 0, scale: 0 }}
          animate={{
            y: -200 - Math.random() * 100,
            opacity: [0, 1, 1, 0],
            scale: [0, 1.2, 1, 0.8],
            x: (Math.random() - 0.5) * 100,
          }}
          transition={{
            duration: 2,
            delay: i * 0.1,
            ease: "easeOut",
          }}
        >
          💶
        </motion.div>
      ))}
    </div>
  );
};

// Radar pulse animation
const RadarPulse = ({ isActive }) => {
  if (!isActive) return null;
  
  return (
    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
      {[0, 1, 2, 3].map((i) => (
        <motion.div
          key={i}
          className="absolute rounded-full border-2"
          style={{ borderColor: COLORS.green }}
          initial={{ width: 40, height: 40, opacity: 0.8 }}
          animate={{
            width: 300,
            height: 300,
            opacity: 0,
          }}
          transition={{
            duration: 2,
            delay: i * 0.5,
            repeat: Infinity,
            ease: "easeOut",
          }}
        />
      ))}
    </div>
  );
};

// Step indicator component
const StepIndicator = ({ step, currentStep, label, icon: Icon }) => {
  const isActive = currentStep >= step;
  const isCurrent = currentStep === step;
  
  return (
    <motion.div 
      className="flex flex-col items-center gap-2"
      initial={{ opacity: 0.3 }}
      animate={{ 
        opacity: isActive ? 1 : 0.3,
        scale: isCurrent ? 1.1 : 1,
      }}
      transition={{ duration: 0.3 }}
    >
      <motion.div
        className={`w-12 h-12 rounded-full flex items-center justify-center ${
          isActive 
            ? 'bg-gradient-to-br from-[#d7de3b] to-[#b8bf2f] text-[#1a2d56]' 
            : 'bg-white/10 text-white/40'
        }`}
        animate={isCurrent ? { 
          boxShadow: ['0 0 0 0 rgba(215, 222, 59, 0)', '0 0 0 15px rgba(215, 222, 59, 0.3)', '0 0 0 0 rgba(215, 222, 59, 0)']
        } : {}}
        transition={{ duration: 1.5, repeat: isCurrent ? Infinity : 0 }}
      >
        <Icon className="w-5 h-5" />
      </motion.div>
      <span className={`text-xs font-medium ${isActive ? 'text-white' : 'text-white/40'}`}>
        {label}
      </span>
    </motion.div>
  );
};

// Main Journey Simulator Component
export default function JourneySimulator() {
  const { t } = useTranslation();
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [showConfetti, setShowConfetti] = useState(false);
  const [showMoney, setShowMoney] = useState(false);
  const [visibleParticipants, setVisibleParticipants] = useState([]);
  const [earnings, setEarnings] = useState(0);
  const [ambassadorEarnings, setAmbassadorEarnings] = useState(0);

  // Session display data
  const sessionData = {
    title: "HIIT Outdoor",
    location: "Parc du Cinquantenaire",
    time: t('dates.today') + " 10h",
    price: 12,
  };

  // Fixed constants for calculations
  const MIN_PARTICIPANTS = 5;

  const resetSimulation = useCallback(() => {
    setIsPlaying(false);
    setCurrentStep(0);
    setShowConfetti(false);
    setShowMoney(false);
    setVisibleParticipants([]);
    setEarnings(0);
    setAmbassadorEarnings(0);
  }, []);

  const startSimulation = useCallback(() => {
    resetSimulation();
    setIsPlaying(true);
    setCurrentStep(1);
  }, [resetSimulation]);

  // Session constants for calculations
  const SESSION_PRICE = 12;
  const COMMISSION_RATE = 0.20;
  const AMBASSADOR_COMMISSION_RATE = 0.10;
  const PAYMENT_FEES_RATE = 0.015;

  // Auto-advance through steps
  useEffect(() => {
    if (!isPlaying) return;

    let timers = [];

    if (currentStep === 1) {
      // Step 1: Session created - wait 2s then go to step 2
      timers.push(setTimeout(() => setCurrentStep(2), 2000));
    } else if (currentStep === 2) {
      // Step 2: Promotion - wait 2.5s then start adding participants
      timers.push(setTimeout(() => setCurrentStep(3), 2500));
    } else if (currentStep === 4) {
      // Step 4: Threshold reached - wait then go to payment
      timers.push(setTimeout(() => {
        setShowConfetti(false);
        setCurrentStep(5);
        setShowMoney(true);
        
        // Calculate earnings based on 5 participants
        const grossRevenue = MIN_PARTICIPANTS * SESSION_PRICE; // 5 x 12€ = 60€
        
        // Regular coach calculation (20% commission)
        const commissionAmount = grossRevenue * COMMISSION_RATE; // 60 x 0.20 = 12€
        const paymentFeesAmount = grossRevenue * PAYMENT_FEES_RATE; // 60 x 0.015 = 0.90€
        const regularEarnings = grossRevenue - commissionAmount - paymentFeesAmount; // 60 - 12 - 0.90 = 47.10€
        
        // Ambassador calculation (10% commission)
        const ambassadorCommission = grossRevenue * AMBASSADOR_COMMISSION_RATE; // 60 x 0.10 = 6€
        const ambassadorNet = grossRevenue - ambassadorCommission - paymentFeesAmount; // 60 - 6 - 0.90 = 53.10€
        
        // Animate earnings
        let currentEarningsVal = 0;
        const finalRegular = Math.round(regularEarnings * 10) / 10; // 47.1€
        const finalAmbassador = Math.round(ambassadorNet * 10) / 10; // 53.1€
        const increment = finalRegular / 20;
        
        const earningsInterval = setInterval(() => {
          currentEarningsVal += increment;
          if (currentEarningsVal >= finalRegular) {
            setEarnings(finalRegular);
            setAmbassadorEarnings(finalAmbassador);
            clearInterval(earningsInterval);
          } else {
            setEarnings(Math.round(currentEarningsVal * 10) / 10);
            setAmbassadorEarnings(Math.round((currentEarningsVal / finalRegular) * finalAmbassador * 10) / 10);
          }
        }, 50);
      }, 2500));
    }

    return () => timers.forEach(clearTimeout);
  }, [isPlaying, currentStep]);

  // Separate useEffect for adding participants
  useEffect(() => {
    if (!isPlaying || currentStep !== 3) return;
    if (visibleParticipants.length >= MIN_PARTICIPANTS) return;
    
    // Add next participant after delay
    const nextIndex = visibleParticipants.length;
    if (nextIndex < PARTICIPANTS.length) {
      const timer = setTimeout(() => {
        setVisibleParticipants(prev => [...prev, PARTICIPANTS[nextIndex]]);
      }, 600);
      
      return () => clearTimeout(timer);
    }
  }, [isPlaying, currentStep, visibleParticipants.length]);

  // Move to step 4 when threshold reached
  useEffect(() => {
    if (currentStep === 3 && visibleParticipants.length >= MIN_PARTICIPANTS) {
      const timer = setTimeout(() => {
        setCurrentStep(4);
        setShowConfetti(true);
      }, 800);
      return () => clearTimeout(timer);
    }
  }, [currentStep, visibleParticipants.length]);

  const steps = [
    { step: 1, label: t('coach.stepCreation'), icon: Sparkles },
    { step: 2, label: t('coach.stepPromotion'), icon: Radio },
    { step: 3, label: t('coach.stepBookings'), icon: Users },
    { step: 4, label: t('coach.stepThreshold'), icon: CheckCircle },
    { step: 5, label: t('coach.stepPayment'), icon: Banknote },
  ];

  return (
    <div className="relative">
      {/* Main container */}
      <div className="bg-gradient-to-br from-white/[0.08] to-white/[0.02] backdrop-blur-sm border border-white/10 rounded-3xl p-6 md:p-10 overflow-hidden">
        
        {/* Header */}
        <div className="text-center mb-8">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full border text-sm font-medium mb-4"
            style={{ background: `${COLORS.green}15`, borderColor: `${COLORS.green}30`, color: COLORS.green }}
          >
            <Zap className="w-4 h-4" />
            {t('coach.interactiveSimulator')}
          </motion.div>
          <h3 className="text-2xl md:text-3xl font-bold text-white mb-2">
            {t('coach.liveCoachExperience')}
          </h3>
          <p className="text-white/60">
            {t('coach.discoverHow')}
          </p>
        </div>

        {/* Step indicators */}
        <div className="flex justify-between items-center mb-10 max-w-lg mx-auto relative">
          {/* Progress line */}
          <div className="absolute top-6 left-0 right-0 h-0.5 bg-white/10">
            <motion.div 
              className="h-full"
              style={{ backgroundColor: COLORS.green }}
              initial={{ width: '0%' }}
              animate={{ width: `${Math.max(0, (currentStep - 1) / 4) * 100}%` }}
              transition={{ duration: 0.5 }}
            />
          </div>
          
          {steps.map((s) => (
            <StepIndicator 
              key={s.step}
              step={s.step}
              currentStep={currentStep}
              label={s.label}
              icon={s.icon}
            />
          ))}
        </div>

        {/* Simulation area */}
        <div className="relative min-h-[320px] bg-gradient-to-b from-[#1a2d56]/50 to-[#1a2d56]/80 rounded-2xl border border-white/10 overflow-hidden">
          
          {/* Confetti overlay */}
          <AnimatePresence>
            {showConfetti && <Confetti count={60} />}
          </AnimatePresence>

          {/* Money overlay */}
          <AnimatePresence>
            {showMoney && <MoneyParticles count={25} />}
          </AnimatePresence>

          {/* Radar pulse for promotion step */}
          <RadarPulse isActive={currentStep === 2} />

          {/* Content based on current step */}
          <div className="relative z-10 p-6 h-full flex flex-col items-center justify-center">
            
            {/* Initial state - Start button */}
            <AnimatePresence mode="wait">
              {currentStep === 0 && (
                <motion.div
                  key="start"
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  className="text-center"
                >
                  <motion.div
                    className="w-24 h-24 mx-auto mb-6 rounded-full flex items-center justify-center"
                    style={{ background: `linear-gradient(135deg, ${COLORS.green}, ${COLORS.greenDark})` }}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <Play className="w-10 h-10 text-[#1a2d56] ml-1" />
                  </motion.div>
                  <Button
                    onClick={startSimulation}
                    className="text-lg px-8 py-6 font-bold rounded-xl"
                    style={{ background: COLORS.green, color: COLORS.darkBlue }}
                  >
                    <Sparkles className="w-5 h-5 mr-2" />
                    {t('coach.createTestSession')}
                  </Button>
                  <p className="text-white/40 text-sm mt-4">
                    {t('coach.clickMagic')}
                  </p>
                </motion.div>
              )}

              {/* Step 1: Session Created */}
              {currentStep === 1 && (
                <motion.div
                  key="step1"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  className="text-center w-full max-w-sm"
                >
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ type: "spring", bounce: 0.5 }}
                    className="mb-6"
                  >
                    <div className="w-20 h-20 mx-auto rounded-2xl flex items-center justify-center mb-4"
                      style={{ background: `linear-gradient(135deg, ${COLORS.green}30, ${COLORS.green}10)`, border: `2px solid ${COLORS.green}` }}>
                      <Calendar className="w-10 h-10" style={{ color: COLORS.green }} />
                    </div>
                  </motion.div>
                  
                  <motion.h4 
                    className="text-xl font-bold text-white mb-4"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.3 }}
                  >
                    {t('coach.sessionCreatedMsg')}
                  </motion.h4>
                  
                  <motion.div
                    className="bg-white/5 rounded-xl p-4 border border-white/10"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.5 }}
                  >
                    <p className="font-bold text-white text-lg mb-2">{sessionData.title}</p>
                    <div className="flex items-center justify-center gap-4 text-white/60 text-sm">
                      <span className="flex items-center gap-1">
                        <MapPin className="w-4 h-4" /> {sessionData.location}
                      </span>
                    </div>
                    <div className="flex items-center justify-center gap-4 text-white/60 text-sm mt-1">
                      <span className="flex items-center gap-1">
                        <Clock className="w-4 h-4" /> {sessionData.time}
                      </span>
                      <span className="flex items-center gap-1">
                        <Euro className="w-4 h-4" /> {sessionData.price}€/{t('common.pers')}
                      </span>
                    </div>
                  </motion.div>
                </motion.div>
              )}

              {/* Step 2: Promotion */}
              {currentStep === 2 && (
                <motion.div
                  key="step2"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="text-center"
                >
                  <motion.div
                    animate={{ rotate: 360 }}
                    transition={{ duration: 3, repeat: Infinity, ease: "linear" }}
                    className="w-20 h-20 mx-auto rounded-full flex items-center justify-center mb-6"
                    style={{ background: `linear-gradient(135deg, ${COLORS.green}30, transparent)`, border: `2px solid ${COLORS.green}` }}
                  >
                    <Radio className="w-10 h-10" style={{ color: COLORS.green }} />
                  </motion.div>
                  
                  <h4 className="text-xl font-bold text-white mb-2">
                    {t('coach.promotionInProgressMsg')}
                  </h4>
                  <p className="text-white/60">
                    {t('coach.motivyaBroadcasts')}
                  </p>
                  
                  <motion.div
                    className="flex justify-center gap-2 mt-6"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.5 }}
                  >
                    {[0, 1, 2].map((i) => (
                      <motion.div
                        key={i}
                        className="w-2 h-2 rounded-full"
                        style={{ backgroundColor: COLORS.green }}
                        animate={{ scale: [1, 1.5, 1], opacity: [0.5, 1, 0.5] }}
                        transition={{ duration: 1, repeat: Infinity, delay: i * 0.2 }}
                      />
                    ))}
                  </motion.div>
                </motion.div>
              )}

              {/* Step 3: Participants joining */}
              {currentStep === 3 && (
                <motion.div
                  key="step3"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="text-center w-full"
                >
                  <h4 className="text-xl font-bold text-white mb-2">
                    {t('coach.registrationsArriving')}
                  </h4>
                  <p className="text-white/60 mb-6">
                    {t('coach.minParticipantsRequired').replace('{count}', MIN_PARTICIPANTS)}
                  </p>
                  
                  <div className="flex flex-wrap justify-center gap-3 mb-4">
                    <AnimatePresence>
                      {visibleParticipants.map((participant, index) => (
                        <motion.div
                          key={participant.name}
                          initial={{ scale: 0, opacity: 0 }}
                          animate={{ scale: 1, opacity: 1 }}
                          exit={{ scale: 0, opacity: 0 }}
                          transition={{ type: "spring", bounce: 0.5 }}
                          className="flex flex-col items-center"
                        >
                          <motion.div
                            className="relative"
                            initial={{ y: -30 }}
                            animate={{ y: 0 }}
                            transition={{ type: "spring", bounce: 0.6 }}
                          >
                            <img 
                              src={participant.avatar} 
                              alt={participant.name}
                              className="w-14 h-14 rounded-full border-2"
                              style={{ borderColor: index < MIN_PARTICIPANTS ? COLORS.green : 'rgba(255,255,255,0.2)' }}
                            />
                            {index < MIN_PARTICIPANTS && (
                              <motion.div
                                initial={{ scale: 0 }}
                                animate={{ scale: 1 }}
                                className="absolute -top-1 -right-1 w-5 h-5 rounded-full flex items-center justify-center"
                                style={{ backgroundColor: COLORS.green }}
                              >
                                <CheckCircle className="w-3 h-3 text-[#1a2d56]" />
                              </motion.div>
                            )}
                          </motion.div>
                          <span className="text-xs text-white/60 mt-1">{participant.name}</span>
                        </motion.div>
                      ))}
                    </AnimatePresence>
                  </div>
                  
                  {/* Progress bar */}
                  <div className="max-w-xs mx-auto">
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-white/60">{t('coach.stepBookings')}</span>
                      <span className="font-bold" style={{ color: visibleParticipants.length >= MIN_PARTICIPANTS ? COLORS.green : 'white' }}>
                        {visibleParticipants.length} / {MIN_PARTICIPANTS}
                      </span>
                    </div>
                    <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                      <motion.div
                        className="h-full rounded-full"
                        style={{ backgroundColor: COLORS.green }}
                        initial={{ width: 0 }}
                        animate={{ width: `${Math.min(100, (visibleParticipants.length / MIN_PARTICIPANTS) * 100)}%` }}
                        transition={{ duration: 0.3 }}
                      />
                    </div>
                  </div>
                </motion.div>
              )}

              {/* Step 4: Threshold reached */}
              {currentStep === 4 && (
                <motion.div
                  key="step4"
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.8 }}
                  className="text-center"
                >
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: [0, 1.2, 1] }}
                    transition={{ duration: 0.5 }}
                    className="w-24 h-24 mx-auto rounded-full flex items-center justify-center mb-6"
                    style={{ background: `linear-gradient(135deg, ${COLORS.green}, ${COLORS.greenDark})` }}
                  >
                    <PartyPopper className="w-12 h-12 text-[#1a2d56]" />
                  </motion.div>
                  
                  <motion.h4 
                    className="text-2xl font-bold mb-2"
                    style={{ color: COLORS.green }}
                    animate={{ scale: [1, 1.05, 1] }}
                    transition={{ duration: 0.5, repeat: 3 }}
                  >
                    {t('coach.thresholdReachedMsg')}
                  </motion.h4>
                  <p className="text-white/80 text-lg mb-4">
                    {t('coach.sessionConfirmedMsg')}
                  </p>
                  <p className="text-white/60">
                    {t('coach.participantsEnrolled').replace('{count}', visibleParticipants.length)}
                  </p>
                  
                  <motion.div
                    className="flex items-center justify-center gap-2 mt-4 text-white/60"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.5 }}
                  >
                    <Heart className="w-4 h-4 text-red-400" />
                    <span>{t('coach.didNothingMsg')}</span>
                  </motion.div>
                </motion.div>
              )}

              {/* Step 5: Payment received */}
              {currentStep === 5 && (
                <motion.div
                  key="step5"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="text-center w-full"
                >
                  <motion.div
                    initial={{ scale: 0, rotate: -180 }}
                    animate={{ scale: 1, rotate: 0 }}
                    transition={{ type: "spring", bounce: 0.5 }}
                    className="w-20 h-20 mx-auto rounded-full flex items-center justify-center mb-4"
                    style={{ background: `linear-gradient(135deg, #22c55e, #16a34a)` }}
                  >
                    <Banknote className="w-10 h-10 text-white" />
                  </motion.div>
                  
                  <h4 className="text-xl font-bold text-white mb-4">
                    {t('coach.paymentReceivedMsg')}
                  </h4>
                  
                  {/* Comparison: Regular vs Ambassador */}
                  <div className="grid grid-cols-2 gap-3 max-w-md mx-auto mb-4">
                    {/* Regular Coach */}
                    <div className="bg-white/5 rounded-xl p-3 border border-white/10 text-sm">
                      <p className="text-white/50 text-xs mb-1">{t('coach.regularCoach')}</p>
                      <p className="text-xl font-bold text-white mb-2">{earnings}€</p>
                      <div className="text-xs text-left space-y-1">
                        <div className="flex justify-between">
                          <span className="text-white/50">5 × 12€</span>
                          <span className="text-white/70">60€</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-white/50">{t('coach.commission')} (20%)</span>
                          <span className="text-red-400">-12€</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-white/50">{t('coach.paymentFees')} (1,5%)</span>
                          <span className="text-red-400">-0,90€</span>
                        </div>
                      </div>
                    </div>
                    
                    {/* Ambassador Coach */}
                    <motion.div 
                      className="rounded-xl p-3 border-2 text-sm relative overflow-hidden"
                      style={{ 
                        background: `linear-gradient(135deg, ${COLORS.green}15, ${COLORS.green}05)`,
                        borderColor: COLORS.green 
                      }}
                      initial={{ scale: 0.95 }}
                      animate={{ scale: 1 }}
                      transition={{ delay: 0.5 }}
                    >
                      <div className="absolute top-0 right-0 px-2 py-0.5 text-xs font-bold rounded-bl"
                        style={{ background: COLORS.green, color: COLORS.darkBlue }}>
                        {t('coach.ambassadorBadge')}
                      </div>
                      <p className="text-white/50 text-xs mb-1">{t('coach.ambassadorCoach')}</p>
                      <p className="text-2xl font-bold mb-2" style={{ color: COLORS.green }}>{ambassadorEarnings}€</p>
                      <div className="text-xs text-left space-y-1">
                        <div className="flex justify-between">
                          <span className="text-white/50">5 × 12€</span>
                          <span className="text-white/70">60€</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-white/50">{t('coach.commission')} (10%)</span>
                          <span className="text-red-400">-6€</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-white/50">{t('coach.paymentFees')} (1,5%)</span>
                          <span className="text-red-400">-0,90€</span>
                        </div>
                      </div>
                    </motion.div>
                  </div>

                  {/* Difference highlight */}
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.8 }}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium mb-4"
                    style={{ background: `${COLORS.green}20`, color: COLORS.green }}
                  >
                    <TrendingUp className="w-4 h-4" />
                    +{(ambassadorEarnings - earnings).toFixed(1)}€ {t('coach.asAmbassadorBonus')}
                  </motion.div>
                  
                  <motion.div
                    className="flex items-center justify-center gap-2"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 1 }}
                  >
                    <CheckCircle className="w-4 h-4 text-green-400" />
                    <span className="text-white/70 text-sm">{t('coach.invoicesAuto')}</span>
                  </motion.div>

                  {/* Restart button */}
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 1.5 }}
                    className="mt-4"
                  >
                    <Button
                      onClick={resetSimulation}
                      variant="outline"
                      size="sm"
                      className="border-white/20 text-white hover:bg-white/10"
                    >
                      <RotateCcw className="w-4 h-4 mr-2" />
                      {t('coach.replayDemo')}
                    </Button>
                  </motion.div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>

        {/* Bottom summary - only show after completion */}
        <AnimatePresence>
          {currentStep === 5 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 20 }}
              transition={{ delay: 2 }}
              className="mt-8 text-center"
            >
              <div className="inline-flex items-center gap-3 px-6 py-3 rounded-full bg-gradient-to-r from-[#d7de3b]/20 to-[#d7de3b]/5 border border-[#d7de3b]/30">
                <Sparkles className="w-5 h-5" style={{ color: COLORS.green }} />
                <span className="text-white font-medium">
                  {t('coach.sessionFlow')}
                </span>
                <ArrowRight className="w-5 h-5" style={{ color: COLORS.green }} />
              </div>
              <p className="text-white/50 text-sm mt-3">
                {t('coach.focusPassion')}
              </p>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
